/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignOffer, EmailBlock, EmailBlockType, EmailContent } from "../types";
import { defaultEmailBlock, newId, STUB_SHOP } from "../stub";
import { usePreview } from "../shared";
import { EmailBlockView, EmailLegalFooter } from "./EmailBlockView";
import { blockLabel, DRAG_MOVE, DRAG_NEW, FONT_STACKS } from "./blocks";

type EmailCanvasProps = {
  email: EmailContent;
  offer: CampaignOffer;
  mobile: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onBlocksChange: (blocks: EmailBlock[]) => void;
};

export const EmailCanvas = defineComponent<EmailCanvasProps>({
  name: "EmailCanvas",
  props: ["email", "offer", "mobile", "selectedId", "onSelect", "onBlocksChange"],
  setup(props) {
    const { t } = useLocalization();
    const preview = usePreview();
    // Index of the gap a drag is hovering (0 = before first block).
    const dropIndex = ref<number | null>(null);

    const blocks = () => props.email.blocks;

    const move = (from: number, to: number) => {
      const next = [...blocks()];
      const [item] = next.splice(from, 1);
      next.splice(to > from ? to - 1 : to, 0, item);
      props.onBlocksChange(next);
    };

    const insert = (type: EmailBlockType, at: number) => {
      const block = defaultEmailBlock(type);
      const next = [...blocks()];
      next.splice(at, 0, block);
      props.onBlocksChange(next);
      props.onSelect(block.id);
    };

    const duplicate = (index: number) => {
      const copy = { ...blocks()[index], id: newId() } as EmailBlock;
      const next = [...blocks()];
      next.splice(index + 1, 0, copy);
      props.onBlocksChange(next);
      props.onSelect(copy.id);
    };

    const remove = (index: number) => {
      const removed = blocks()[index];
      props.onBlocksChange(blocks().filter((_, i) => i !== index));
      if (removed.id === props.selectedId) props.onSelect(null);
    };

    const onDragOverBlock = (e: DragEvent, index: number) => {
      const types = e.dataTransfer?.types ?? [];
      if (!types.includes(DRAG_NEW) && !types.includes(DRAG_MOVE)) return;
      e.preventDefault();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      dropIndex.value = e.clientY < rect.top + rect.height / 2 ? index : index + 1;
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      const at = dropIndex.value ?? blocks().length;
      dropIndex.value = null;
      const newType = e.dataTransfer?.getData(DRAG_NEW);
      if (newType) return insert(newType as EmailBlockType, at);
      const fromId = e.dataTransfer?.getData(DRAG_MOVE);
      const from = blocks().findIndex((b) => b.id === fromId);
      if (from !== -1 && from !== at && from + 1 !== at) move(from, at);
    };

    const DropLine = (index: number) =>
      dropIndex.value === index ? (
        <div class="pointer-events-none relative z-10 h-0">
          <div class="absolute inset-x-2 -top-0.5 h-1 rounded-full bg-primary shadow" />
        </div>
      ) : null;

    const ActionBar = (index: number) => (
      <div class="absolute -right-1 top-1 z-20 flex translate-x-full flex-col gap-1 pl-2">
        {[
          { icon: "arrow_upward", disabled: index === 0, run: () => move(index, index - 1), label: "move_up" },
          { icon: "arrow_downward", disabled: index === blocks().length - 1, run: () => move(index, index + 2), label: "move_down" },
          { icon: "content_copy", disabled: false, run: () => duplicate(index), label: "duplicate" },
          { icon: "delete", disabled: false, run: () => remove(index), label: "delete" },
        ].map((a) => (
          <button
            key={a.icon}
            type="button"
            class={["btn btn-square btn-xs shadow", a.icon === "delete" ? "btn-error" : "bg-base-100"]}
            disabled={a.disabled}
            title={t(`campaigns.studio_email.${a.label}`)}
            onClick={(e) => {
              e.stopPropagation();
              a.run();
            }}
          >
            <span class="material-symbols-outlined text-sm">{a.icon}</span>
          </button>
        ))}
      </div>
    );

    return () => {
      const { theme } = props.email;
      return (
        <div
          class="min-h-full px-10 py-6"
          style={{ background: theme.background_color }}
          onClick={() => props.onSelect(null)}
        >
          {/* Inbox header */}
          <div
            class={["mx-auto mb-4 rounded-xl bg-base-100 p-3 text-sm shadow-sm", props.mobile ? "max-w-[375px]" : "max-w-[600px]"]}
          >
            <div class="flex items-center gap-3">
              <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 font-bold text-primary">
                {props.email.from_name.slice(0, 1)}
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex justify-between gap-2">
                  <span class="truncate font-semibold">{props.email.from_name || STUB_SHOP.name}</span>
                  <span class="shrink-0 text-xs text-base-content/50">09:41</span>
                </div>
                <div class="truncate font-medium">{preview.render(props.email.subject) || t("campaigns.studio_email.no_subject")}</div>
                <div class="truncate text-base-content/60">{preview.render(props.email.preheader)}</div>
              </div>
            </div>
          </div>

          <div
            class={["mx-auto overflow-visible shadow-lg transition-all", props.mobile ? "max-w-[375px]" : "max-w-[600px]"]}
            style={{
              background: theme.content_background,
              borderRadius: `${theme.radius}px`,
              fontFamily: FONT_STACKS[theme.font],
            }}
            onDragleave={(e) => {
              if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) dropIndex.value = null;
            }}
            onDragover={(e) => {
              if (blocks().length === 0) {
                e.preventDefault();
                dropIndex.value = 0;
              }
            }}
            onDrop={onDrop}
          >
            {blocks().length === 0 && (
              <div class="flex h-48 flex-col items-center justify-center gap-2 text-base-content/50">
                <span class="material-symbols-outlined text-4xl">drag_indicator</span>
                {t("campaigns.studio_email.empty_canvas")}
              </div>
            )}
            {blocks().map((block, index) => {
              const selected = block.id === props.selectedId;
              return (
                <div key={block.id}>
                  {DropLine(index)}
                  <div
                    class={[
                      "group relative cursor-pointer outline-offset-[-2px] transition-[outline-color]",
                      selected ? "outline outline-2 outline-primary" : "hover:outline hover:outline-1 hover:outline-primary/40",
                    ]}
                    draggable={true}
                    onDragstart={(e) => {
                      e.dataTransfer?.setData(DRAG_MOVE, block.id);
                      if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragend={() => (dropIndex.value = null)}
                    onDragover={(e) => onDragOverBlock(e, index)}
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onSelect(block.id);
                    }}
                  >
                    {selected && (
                      <span class="absolute -top-5 left-0 z-20 rounded-t bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-content">
                        {t(blockLabel(block.type))}
                      </span>
                    )}
                    <EmailBlockView block={block} theme={theme} offer={props.offer} mobile={props.mobile} />
                    {selected && ActionBar(index)}
                  </div>
                </div>
              );
            })}
            {DropLine(blocks().length)}
            <EmailLegalFooter theme={theme} />
          </div>
        </div>
      );
    };
  },
});
